'use client'

import { ReactNode, useState } from 'react'

import { useAppRouter } from '@/shared/lib/router'
import { useForm, FormProvider } from 'react-hook-form'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'

import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { cn } from '@/shared/lib/utils'

const schema = z.object({
  email: z
    .string({ required_error: 'E-mail é obrigatório' })
    .email('E-mail inválido')
})

type FormData = z.infer<typeof schema>

interface ForgotPasswordFormProps {
  className?: string
  children?: ReactNode
  onSuccess?: () => void
}

export function ForgotPasswordForm({
  className = '',
  children,
  onSuccess
}: ForgotPasswordFormProps) {
  const methods = useForm<FormData>({ resolver: zodResolver(schema) })
  const router = useAppRouter()
  const { httpClient, setCurrentEmail } = useAuthContext()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async (data: FormData) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      setCurrentEmail?.(data.email)
      await httpClient.post('/auth/reset-password', { email: data.email })
      onSuccess?.()
      router.push('/reset-password')
    } catch (error: unknown) {
      const err = error as { message?: string }
      methods.setError('email', {
        type: 'custom',
        message:
          err?.message || 'Não foi possível enviar o código. Tente novamente.'
      })
    }
    setIsSubmitting(false)
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={cn('grid gap-4', className)}
      >
        {children}
      </form>
    </FormProvider>
  )
}
